import LoadingSpinner from 'components/shared/LoadingSpinner';
import { Button } from 'components/ui/button';
import useRemoveFavoriteDocument from 'domain/documents/hooks/useRemoveFavoriteDocument';
import { DocumentLink } from 'domain/documents/schemas/document';
import { Download, ExternalLink, HeartMinus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDownload from 'utils/useDownload';

type FavoriteListItemProps = {
  document: DocumentLink;
};

export default function FavoriteListItem({ document }: FavoriteListItemProps) {
  const { t } = useTranslation('searchPage');
  const { download, isDownloading } = useDownload();
  const { mutate: removeFavoriteDocument, isPending: isRemovingFavorite } = useRemoveFavoriteDocument();

  const hasFile = document.downloadUrl !== '';
  // Un document qui pointe ailleurs s'ouvre dans une nouvelle page au lieu de se télécharger.
  const hasLink = document.linkUrl !== '';

  const handleDownloadClick = () => {
    download(document.downloadUrl, document.filename);
  };

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <p className="overflow-hidden font-bold text-ellipsis whitespace-nowrap" title={document.title}>
        {document.title}
      </p>
      <div className="mr-2 flex items-center gap-2">
        <Button
          className="rounded-xl bg-red-500 hover:bg-red-700"
          onClick={() => removeFavoriteDocument(document)}
          disabled={isRemovingFavorite}
        >
          {isRemovingFavorite ? <LoadingSpinner isMini isWhite /> : <HeartMinus />}
        </Button>
        {hasLink ? (
          <Button asChild variant="yellowFull" className="rounded-xl">
            <a
              href={document.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('suggestion.ariaLabelLink', { title: document.title })}
            >
              <ExternalLink />
            </a>
          </Button>
        ) : (
          <Button
            variant="yellowFull"
            className="rounded-xl"
            onClick={handleDownloadClick}
            disabled={!hasFile || isDownloading}
          >
            {isDownloading ? <LoadingSpinner isMini /> : <Download />}
          </Button>
        )}
      </div>
    </div>
  );
}
